import * as React from 'react';

import { useSelector } from 'react-redux';
import { ActionLink, ConfirmationInput, UIText1 } from '@cian/ui-kit';
import {
  trackAntifraudPopupChangePhone,
  trackAntifraudPopupConfirm,
  trackAntifraudPopupResendCode,
  trackAntifraudPopupShow,
} from './tracking/trackAntifraudPopup';

import { PhoneConfirmation } from '../../components/PhoneConfirmation';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { selectPage, selectPageExtra } from '../../selectors/analytics';

import {
  selectFormPhone,
  selectIsFormSubmitting,
  selectFormPhoneVerificationError,
  selectFormLastAttemptTimestamp,
  selectFormSecondsToRetry,
} from '../../selectors/requestFormV2';
import { setFormStep, setConformationCode, clearErrors, clearConformationCode } from '../../slices';
import { createConsultingRequestWithVerification } from '../../thunks/consultingRequest';
import { requestPhoneVerificationCode } from '../../thunks/phoneVerification';
import { EFormStep } from '../../types/requestForm/requestFormV2';
import { CountdownTimer } from '../CountdownTimer/CountdownTimer';
import { preparePhoneDisplay } from './utils/preparePhoneDisplay';

export const PhoneConfirmationContainer = (): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const isLoading = useSelector(selectIsFormSubmitting);
  const phone = useSelector(selectFormPhone) || '';

  const secondsToRetry = useSelector(selectFormSecondsToRetry);
  const lastAttemptTimestamp = useSelector(selectFormLastAttemptTimestamp);

  const errorText = useSelector(selectFormPhoneVerificationError);

  const page = useSelector(selectPage);
  const extra = useSelector(selectPageExtra);

  const [value, setValue] = React.useState('');
  const [isTouched, setIsTouched] = React.useState(false);
  const [isResendAvailable, setIsResendAvailable] = React.useState(false);

  const handleComplete = (val: string) => {
    trackAntifraudPopupConfirm(page, extra);
    dispatch(setConformationCode(val));
    dispatch(createConsultingRequestWithVerification());
    setIsTouched(false);
  };

  const handleChangeNumber = () => {
    trackAntifraudPopupChangePhone(page, extra);
    dispatch(clearErrors());
    dispatch(clearConformationCode());
    dispatch(setFormStep(EFormStep.Contacts));
  };

  const handleChangeValue = (val: string) => {
    setValue(val);
    if (!isTouched && errorText) {
      dispatch(clearErrors());
    }
    setIsTouched(true);
  };

  const handleTimeoutExpired = React.useCallback(() => {
    setIsResendAvailable(true);
  }, []);

  const handleResendClick = React.useCallback(() => {
    setValue('');
    trackAntifraudPopupResendCode(page, extra);
    dispatch(requestPhoneVerificationCode());
  }, [dispatch, extra, page]);

  React.useEffect(() => {
    trackAntifraudPopupShow(page, extra);
  }, [extra, page]);

  return (
    <PhoneConfirmation
      error={errorText || undefined}
      phone={preparePhoneDisplay(phone)}
      changeNumber={<ActionLink onClick={handleChangeNumber}>Изменить номер</ActionLink>}
      confirmationInput={
        <ConfirmationInput
          autoFocus
          disabled={isLoading}
          invalid={!!errorText}
          format="9999"
          value={value}
          dataTestId="PhoneConfirmationInput"
          onChange={handleChangeValue}
          onComplete={handleComplete}
        />
      }
      countdown={
        !isLoading && lastAttemptTimestamp && !isResendAvailable ? (
          <UIText1 display="inline" color="icon-secondary-default">
            Отправить код повторно через{' '}
            {
              <CountdownTimer
                fromTimestamp={lastAttemptTimestamp}
                timeout={secondsToRetry}
                onTimeoutExpired={handleTimeoutExpired}
              />
            }{' '}
            сек
          </UIText1>
        ) : null
      }
      resend={
        isLoading ? (
          <UIText1 color="text-primary-disabled">Отправить код повторно</UIText1>
        ) : (
          <ActionLink onClick={handleResendClick}>Отправить код повторно</ActionLink>
        )
      }
    />
  );
};
